'use client';

import { useFormContext, useWatch } from 'react-hook-form';

import { Icon, type IconName } from '@eliadmin/core/src/components/nt/ui/icon';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { PERSONA_FIELDS, type RegisterFormInput } from './register-schema';

/**
 * Figma „Segment“ under „Registrujem sa ako“ (221:13538 / 221:13989, mobile 290:33320): #F5F6F3,
 * radius 12 (16 on the phone), padding 4, gap 4; the options 12 / 16 px padding (10 / 8 on the
 * phone), 18 px icon 8 px from the label; the chosen one #0D110D with Body/M Bold white, the other
 * Body/M #5D655D. Native radios: arrows switch, the group is named by the heading.
 */
export function AccountTypeSwitch({
  labelledBy,
  companyLabel,
}: {
  labelledBy: string;
  /** „Firma / podnikateľ“ (the checkout's word). */
  companyLabel: string;
}) {
  const { t } = useTranslations();
  const { control, setValue, clearErrors } = useFormContext<RegisterFormInput>();
  const company = !!useWatch({ control, name: 'company_buy' });

  const options: { value: boolean; label: string; icon: IconName }[] = [
    { value: false, label: t('text2234'), icon: 'user' },
    { value: true, label: companyLabel, icon: 'buildings' },
  ];

  return (
    <div
      role="radiogroup"
      aria-labelledby={labelledBy}
      className="grid grid-cols-2 gap-1 rounded-2xl bg-[#F5F6F3] p-1 sm:rounded-xl"
    >
      {options.map(option => {
        const selected = option.value === company;
        return (
          <label
            key={String(option.value)}
            className={cn(
              'flex min-w-0 cursor-pointer items-center justify-center gap-1.5 rounded-xl px-2 py-2.5 transition-colors has-[input:focus-visible]:shadow-focus sm:gap-2 sm:px-4 sm:py-3',
              selected
                ? 'nt-body-m-bold bg-[#0D110D] text-white'
                : 'nt-body-m text-[#5D655D] hover:bg-[#EBEDE8] hover:text-[#0D110D]',
            )}
          >
            <input
              type="radio"
              name="register-type"
              value={option.value ? 'company' : 'person'}
              checked={selected}
              onChange={() => {
                // Errors of fields that are gone or optional now would stay; the submit re-checks.
                clearErrors([...PERSONA_FIELDS]);
                setValue('company_buy', option.value, { shouldDirty: true });
              }}
              className="sr-only"
            />
            <Icon name={option.icon} size={18} className="shrink-0" />
            {/* No `truncate`: its clip cuts the caron of „ľ“ (Figtree draws it past the advance). */}
            <span className="min-w-0 text-center">{option.label}</span>
          </label>
        );
      })}
    </div>
  );
}
